import React, { useState, useEffect } from 'react';
import type { StaffRole } from '../../shared/types';
import { getBackendService } from '../../shared/services/apiConfig';
import BanterLoader from '../../shared/design/components/BanterLoader';
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    updateDoc,
    doc,
    serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../shared/services/firebase';

interface StaffMemberLocal {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: StaffRole;
    isActive: boolean;
    createdAt: string;
}

const ROLE_LABELS: Record<StaffRole, string> = {
    OWNER: 'Propriétaire',
    MANAGER: 'Manager',
    STAFF: 'Staff',
};

const ROLE_COLORS: Record<StaffRole, string> = {
    OWNER: 'bg-purple-100 text-purple-800',
    MANAGER: 'bg-blue-100 text-blue-800',
    STAFF: 'bg-gray-100 text-gray-700',
};

const StaffManager: React.FC = () => {
    const [staff, setStaff] = useState<StaffMemberLocal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<StaffRole>('STAFF');
    const [inviting, setInviting] = useState(false);
    const [revoking, setRevoking] = useState<string | null>(null);
    const [restaurantId, setRestaurantId] = useState<string | null>(null);

    useEffect(() => {
        const init = async () => {
            const backend = await getBackendService();
            const user = backend.getCurrentUser();
            const rId = user?.restaurantId ? String(user.restaurantId) : null;
            setRestaurantId(rId);
            if (rId) await loadStaff(rId);
            else setLoading(false);
        };
        init();
    }, []);

    const loadStaff = async (rId: string) => {
        setLoading(true);
        setError(null);
        try {
            const q = query(
                collection(db, 'staff'),
                where('restaurantId', '==', rId)
            );
            const snap = await getDocs(q);
            const members: StaffMemberLocal[] = snap.docs.map(d => ({
                id: d.id,
                email: d.data().email || '',
                firstName: d.data().firstName || '',
                lastName: d.data().lastName || '',
                role: d.data().role || 'STAFF',
                isActive: d.data().isActive !== false,
                createdAt: d.data().createdAt || '',
            }));
            setStaff(members);
        } catch (e: any) {
            setError('Impossible de charger la liste du staff.');
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!restaurantId || !inviteEmail) return;
        setInviting(true);
        setError(null);
        try {
            // Check if already invited
            const q = query(
                collection(db, 'staff'),
                where('restaurantId', '==', restaurantId),
                where('email', '==', inviteEmail)
            );
            const existing = await getDocs(q);
            if (!existing.empty) {
                setError('Ce membre est déjà invité.');
                return;
            }

            const nameParts = inviteEmail.split('@')[0].split('.');
            await addDoc(collection(db, 'staff'), {
                restaurantId,
                email: inviteEmail,
                firstName: nameParts[0] || '',
                lastName: nameParts[1] || '',
                role: inviteRole,
                isActive: true,
                createdAt: serverTimestamp(),
            });

            // Open email client so the restaurateur can notify the invitee
            const subject = encodeURIComponent('Invitation à rejoindre l\'équipe sur atable');
            const body = encodeURIComponent(
                `Bonjour,\n\nVous avez été invité(e) à rejoindre l'équipe en tant que ${inviteRole} sur atable.\n\nConnectez-vous ou créez votre compte sur l'application atable pour accéder à votre espace.\n\nCordialement,`
            );
            window.open(`mailto:${inviteEmail}?subject=${subject}&body=${body}`);

            setInviteEmail('');
            setShowInviteForm(false);
            await loadStaff(restaurantId);
        } catch (e: any) {
            setError('Erreur lors de l\'invitation.');
        } finally {
            setInviting(false);
        }
    };

    const handleRevoke = async (staffId: string, name: string) => {
        if (!restaurantId) return;
        if (!confirm(`Révoquer l'accès de ${name} ?`)) return;
        setRevoking(staffId);
        try {
            await updateDoc(doc(db, 'staff', staffId), { isActive: false });
            await loadStaff(restaurantId);
        } catch (e: any) {
            setError('Erreur lors de la révocation.');
        } finally {
            setRevoking(null);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="page-title">Équipe</h1>
                    <p className="page-subtitle">Invitez vos serveurs et gérez leurs accès à l'espace restaurateur.</p>
                </div>
                <button
                    onClick={() => setShowInviteForm(true)}
                    className="inline-flex h-11 w-fit flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-brand-500 px-5 text-sm font-semibold text-cream shadow-[var(--shadow-brand)] transition-colors hover:bg-brand-600"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Inviter un membre
                </button>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
                    {error}
                </div>
            )}

            {/* Invite Form */}
            {showInviteForm && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Inviter un nouveau membre</h2>
                    <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="email"
                            placeholder="email@exemple.com"
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            required
                            className="flex-1 px-4 py-2.5 border border-gray-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        />
                        <select
                            value={inviteRole}
                            onChange={e => setInviteRole(e.target.value as StaffRole)}
                            className="px-4 py-2.5 border border-gray-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
                        >
                            <option value="STAFF">Staff</option>
                            <option value="MANAGER">Manager</option>
                        </select>
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={inviting}
                                className="px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm transition-colors"
                            >
                                {inviting ? 'Envoi...' : 'Inviter'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowInviteForm(false)}
                                className="px-4 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 text-sm transition-colors"
                            >
                                Annuler
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Staff List */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-brand-primary">
                        <BanterLoader size="md" centered label="Chargement du staff…" />
                    </div>
                ) : staff.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <p className="text-gray-900 font-semibold">Aucun membre staff</p>
                        <p className="text-gray-500 text-sm mt-1">Invitez votre première équipe pour déléguer l'accès.</p>
                    </div>
                ) : (
                    // Défilement horizontal sur mobile plutôt qu'un tableau qui déborde de l'écran.
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Membre</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Rôle</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Statut</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {staff.map(member => (
                                <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm">
                                                {(member.firstName?.[0] || member.email[0]).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-gray-900 text-sm">
                                                    {member.firstName && member.lastName
                                                        ? `${member.firstName} ${member.lastName}`
                                                        : member.email}
                                                </p>
                                                <p className="text-gray-500 text-xs">{member.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${ROLE_COLORS[member.role]}`}>
                                            {ROLE_LABELS[member.role]}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${member.isActive ? 'text-green-700' : 'text-gray-400'}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${member.isActive ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                                            {member.isActive ? 'Actif' : 'Révoqué'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        {member.role !== 'OWNER' && member.isActive && (
                                            <button
                                                onClick={() => handleRevoke(member.id, member.firstName || member.email)}
                                                disabled={revoking === member.id}
                                                className="text-xs font-semibold text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors"
                                            >
                                                {revoking === member.id ? 'Révocation...' : 'Révoquer'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StaffManager;
